import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { tags } from "@/mocks/tags";
import { videos } from "@/mocks/videos";
import VideoBrowser from "@/components/feature/VideoBrowser";
import VideoCard from "@/components/feature/VideoCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import { sortVideos } from "@/utils/videoFilters";

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";
  const [keyword, setKeyword] = useState(query);

  useEffect(() => {
    setKeyword(query);
  }, [query]);

  useDocumentTitle(
    keyword ? `${keyword}的在线视频 - 啪啪视频` : "视频搜索 - 啪啪视频",
    keyword
      ? `在啪啪视频搜索“${keyword}”，为你找到相关的高清视频内容，支持按分类与标签进一步筛选。`
      : "在啪啪视频输入关键词，搜索你感兴趣的高清视频内容。",
    keyword ? `${keyword},${keyword}视频,在线视频,啪啪视频` : "视频搜索,在线视频,高清视频,啪啪视频",
  );

  const matched = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    if (!q) return videos;
    return videos.filter((video) => {
      const tagNames = video.tags
        .map((slug) => tags.find((tag) => tag.slug === slug)?.name ?? "")
        .join(" ");
      const haystack = `${video.title} ${video.description} ${video.categoryName} ${video.author} ${tagNames}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [keyword]);

  const recommended = useMemo(() => sortVideos(videos, "hot").slice(0, 4), []);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    if (value) {
      setSearchParams({ q: value });
    } else {
      setSearchParams({});
    }
  };

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索" }]} />

      <header className="mt-4 rounded-lg border border-background-200 bg-background-50 p-6">
        <h1 className="font-heading text-2xl font-bold text-foreground-950">
          {keyword ? `“${keyword}”的搜索结果` : "视频搜索"}
        </h1>
        <p className="mt-1.5 text-sm text-foreground-500">
          {keyword ? "根据关键词为你匹配相关视频，可继续使用筛选条件缩小范围。" : "输入你感兴趣的关键词，发现更多高清视频。"}
        </p>
        <form onSubmit={submit} className="mt-5 flex flex-col gap-3 sm:flex-row">
          <div className="flex flex-1 items-center gap-2 rounded-full border border-background-300 bg-background-100 px-4 py-2.5">
            <i className="ri-search-line text-base text-foreground-400"></i>
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索视频标题、作者或标签"
              aria-label="搜索视频关键词"
              className="w-full bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
            />
          </div>
          <button
            type="submit"
            className="cursor-pointer whitespace-nowrap rounded-full bg-primary-500 px-7 py-2.5 text-sm font-medium text-background-50 transition-colors duration-200 hover:bg-primary-600"
          >
            <i className="ri-search-line mr-1.5"></i>
            搜索
          </button>
        </form>
      </header>

      <p className="mt-5 text-sm text-foreground-500">
        共找到 <span className="font-semibold text-primary-600">{matched.length}</span> 个相关视频
      </p>

      <div className="mt-5">
        {matched.length === 0 ? (
          <div className="flex flex-col gap-10">
            <EmptyState
              icon="ri-search-eye-line"
              title={`没有找到与“${keyword}”相关的视频`}
              description="换个关键词试试，或者浏览下面的热门推荐，说不定会有惊喜。"
            />
            <section>
              <h2 className="mb-5 font-heading text-xl font-bold text-foreground-950">为你推荐</h2>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                {recommended.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>
            </section>
          </div>
        ) : (
          <VideoBrowser
            videos={matched}
            showCategory
            showTag
            emptyTitle="当前筛选条件下没有视频"
            emptyDescription="你搜索的视频可能在更宽松的筛选条件下。试着重置筛选，查看更多结果。"
          />
        )}
      </div>
    </div>
  );
}